import { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

export default function TourismComponent() {
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, sections.length);
  }, []);

  // Scroll to next section
  const scrollToNext = () => {
    if (activeSection < sections.length - 1) {
      const nextIndex = activeSection + 1;
      setActiveSection(nextIndex);
      sectionRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle scroll events to determine active section
  useEffect(() => {
    const handleScroll = () => {
      const pageTop = window.pageYOffset;
      const pageBottom = pageTop + window.innerHeight;
      
      sectionRefs.current.forEach((ref, index) => {
        if (!ref) return;
        
        const el = ref;
        const rect = el.getBoundingClientRect();
        const elTop = rect.top + pageTop;
        const elBottom = elTop + rect.height;
        
        // If element is in viewport
        if (elTop < pageBottom && elBottom > pageTop) {
          setActiveSection(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const sections = [
    {
      title: "Leverage India Heritage Tourism Potential",
      content: "Discover the rich cultural heritage and natural beauty of India through our innovative tourism initiative. This collaboration brings together traditional values and modern approaches to create unforgettable experiences for visitors from around the world.",
      image: "/images/heritage.jpg",
      imageAlt: "India Heritage Sites Overview"
    },
    {
      title: "Promote Iconic Heritage Sites",
      content: "Develop and promote India's rich heritage sites, including ancient Buddhist stupas, Lepakshi Temple, Amaravati sculptures, and historical forts, as global tourist destinations. Each site tells a unique story of the region's rich history spanning thousands of years.",
      image: "/images/lepakshi.jpg",
      imageAlt: "Lepakshi Temple"
    },
    {
      title: "Curate Cultural Tourism Experiences",
      content: "Create immersive tourism packages featuring traditional art forms like Kuchipudi dance, local crafts such as Kalamkari art, and authentic culinary experiences showcasing India cuisine. These hands-on experiences allow visitors to connect deeply with the cultural fabric of the region.",
      image: "/images/kuchipudi.jpg",
      imageAlt: "Kuchipudi Dance Performance"
    },
    {
      title: "Collaborate on Sustainable Tourism Infrastructure",
      content: "Partner with Dutch experts to design eco-friendly tourist facilities, smart visitor management systems, and heritage conservation projects. This international collaboration brings cutting-edge sustainable practices to preserve India's natural beauty and cultural sites for generations to come.",
      image: "/images/tourism1.jpg",
      imageAlt: "Sustainable Tourism Facility"
    },
    {
      title: "Joint Cultural Festivals",
      content: "Organize annual cultural exchange festivals featuring Dutch art, traditions, and India's cultural heritage to create a cross-cultural tourism appeal. These vibrant celebrations showcase the unique aspects of both cultures while fostering international friendship and understanding.",
      image: "/images/culture.jpg",
      imageAlt: "Cultural Festival"
    },
    {
      title: "Outcomes",
      outcomes: [
        {
          title: "Global Heritage Tourism Hub",
          content: "Position India as a premier destination for cultural and heritage tourism worldwide."
        },
        {
          title: "International Tourist Footfall",
          content: "Increase international tourist arrivals and boost tourism revenues, supporting the local economy."
        },
        {
          title: "Cultural Diplomacy",
          content: "Enhance cultural exchange and promote cross-cultural understanding between India and the Netherlands."
        },
        {
          title: "Employment Opportunities",
          content: "Create jobs in hospitality, arts, and tourism sectors throughout the region."
        },
        {
          title: "Heritage Preservation",
          content: "Preserve and sustainably manage historical sites and cultural traditions for future generations."
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {sections.map((section, index) => (
        <div
          key={index}
          ref={el => sectionRefs.current[index] = el}
          className={`min-h-screen flex flex-col justify-center items-center p-8 ${
            index % 2 === 0 ? 'bg-indigo-50' : 'bg-white'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-6">{section.title}</h2>
            
            {/* Regular section with content and image */}
            {!section.outcomes && (
              <>
                <p className="text-xl text-gray-700 mb-12">{section.content}</p>
                <div className="rounded-xl overflow-hidden shadow-xl mb-10">
                  <img 
                    src={section.image} 
                    alt={section.imageAlt} 
                    className="w-full object-cover" 
                  />
                </div>
              </>
            )}
            
            {/* Outcomes section */}
            {section.outcomes && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mt-8">
                {section.outcomes.map((outcome, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-lg shadow-lg">
                    <h3 className="text-xl font-bold text-indigo-800 mb-3">{outcome.title}</h3>
                    <p className="text-gray-700">{outcome.content}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Show scroll down indicator if not the last section */}
            {index < sections.length - 1 && (
              <button 
                onClick={scrollToNext}
                className="mt-10 text-indigo-600 hover:text-indigo-800 flex flex-col items-center animate-bounce"
              >
                <span className="mb-2">Scroll for more</span>
                <ChevronDown className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}